// Home — the modules landing grid (Omnix-style). Permission-gated cards that link
// into each area of AIMS. This is the logged-in landing page at "/".

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { ViewInAr, AddCircle } from '@mui/icons-material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BuildIcon from '@mui/icons-material/Build';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HandymanIcon from '@mui/icons-material/Handyman';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EventNoteIcon from '@mui/icons-material/EventNote';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

const can = (flag) => localStorage.getItem(flag) === '1';

// Module catalogue — order + permission gates mirror the app's routes.
function buildModules() {
  return [
    // Main section
    { label: 'Assets', desc: 'Search, scan & view asset records', to: '/assets', icon: <ViewInAr />, show: true, section: 'main' },
    { label: 'Create Asset', desc: 'Register a new asset', to: '/createAsset', icon: <AddCircle />, show: can('canAddAsset'), section: 'main' },
    { label: 'Scheduling', desc: 'Plan & track site visits', to: '/scheduling', icon: <CalendarMonthIcon />, show: can('canViewSchedule') || can('canManageSchedule'), section: 'main' },
    { label: 'Workshop', desc: 'Jobs, calibrations & teardowns', to: '/workshop', icon: <HandymanIcon />, show: can('canManageWorkshop'), section: 'main' },
    { label: 'Inspection', desc: 'Detailed asset inspections', to: '/inspection', icon: <FactCheckIcon />, show: can('canUseDetailedInspection') && !can('canManageWorkshop'), section: 'main' },
    { label: 'Defects', desc: 'Defect reports & discussions', to: '/defects', icon: <ReportProblemIcon />, show: can('canAddDefect') || can('canInspectAsset'), section: 'main' },
    { label: 'Admin', desc: 'Users, companies & configuration', to: '/admin', icon: <AdminPanelSettingsIcon />, show: can('canRegisterUser'), section: 'main' },
    // Reporting section — view/export things for review
    { label: 'Reporting Dashboard', desc: 'Platform reporting dashboard', to: '/reporting/platform-dashboard', icon: <AssessmentIcon />, show: can('canViewPlatformDashboard'), section: 'reporting' },
    { label: 'Schedule Report', desc: 'Reporting over scheduled visits', to: '/reporting/schedule-report', icon: <EventNoteIcon />, show: can('canManageSchedule'), section: 'reporting' },
    { label: 'Admin Timesheet Report', desc: 'Timesheet hours by user', to: '/reporting/admin-timesheet-report', icon: <SummarizeIcon />, show: can('canAdminReport'), section: 'reporting' },
    { label: 'Open Repairs', desc: 'Open repairs & C&Ds for review', to: '/openRepairs', icon: <BuildIcon />, show: can('canInspectAsset'), section: 'reporting' },
    { label: 'Asset Export', desc: 'Export the asset register', to: '/exportfile', icon: <FileDownloadIcon />, show: can('canSearch'), section: 'reporting' },
    { label: 'Timesheets', desc: 'Review & log work timesheets', to: '/timesheetReview', icon: <ReceiptLongIcon />, show: can('canInspectAsset'), section: 'reporting' },
  ].filter((m) => m.show);
}

export default function Home() {
  const navigate = useNavigate();
  const modules = buildModules();
  const name = (localStorage.getItem('name') || '').replace(/"/g, '');
  const [reportingOpen, setReportingOpen] = useState(false);

  const mainItems = modules.filter((m) => m.section !== 'reporting');
  const reportingItems = modules.filter((m) => m.section === 'reporting');

  // Main grid = main modules + (if any reporting items) a "Reporting" tile that
  // reveals the report/export items to choose from.
  const gridItems = [...mainItems];
  if (reportingItems.length > 0) {
    gridItems.push({
      label: 'Reporting',
      desc: 'View reports & exports for review',
      icon: <AssessmentIcon />,
      toggle: true,
      open: reportingOpen,
      onClick: () => setReportingOpen((o) => !o),
    });
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--text)' }}>
        {name ? `Welcome back, ${name}` : 'Welcome to AIMS'}
      </Typography>
      <Typography variant="body2" sx={{ color: 'var(--muted)', mb: 3 }}>
        Choose an area to get started.
      </Typography>

      <ModuleGrid items={gridItems} navigate={navigate} />

      {reportingOpen && reportingItems.length > 0 && (
        <>
          <Typography sx={{ mt: 4, mb: 1.5, fontSize: 12, fontWeight: 700, letterSpacing: '.7px', textTransform: 'uppercase', color: 'var(--faint)' }}>
            Reporting
          </Typography>
          <ModuleGrid items={reportingItems} navigate={navigate} />
        </>
      )}
    </Box>
  );
}

function ModuleGrid({ items, navigate }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
      {items.map((m) => {
        const activate = m.onClick ? m.onClick : () => navigate(m.to);
        return (
        <Box
          key={m.to || m.label}
          role="button"
          tabIndex={0}
          onClick={activate}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') activate(); }}
          sx={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: 2,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r)',
            boxShadow: 'var(--shadow-sm)',
            transition: 'border-color 120ms, transform 120ms, box-shadow 120ms',
            '&:hover': { borderColor: 'var(--primary)', transform: 'translateY(-2px)', boxShadow: 'var(--shadow)' },
            '&:focus-visible': { outline: '2px solid var(--primary)', outlineOffset: 2 },
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: 44,
              height: 44,
              borderRadius: '10px',
              background: 'var(--primary-tint)',
              color: 'var(--primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& svg': { fontSize: 24 },
            }}
          >
            {m.icon}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 700, color: 'var(--text)' }}>{m.label}</Typography>
            <Typography variant="body2" sx={{ color: 'var(--muted)' }}>{m.desc}</Typography>
          </Box>
          {m.toggle && (
            <Box sx={{ color: 'var(--muted)', alignSelf: 'center' }}>
              {m.open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
          )}
        </Box>
        );
      })}
    </Box>
  );
}
